import * as React from "react";
import { SimpleMarkdown } from "../../shared/components/SimpleMarkdown";
import {
  ThoughtProcessPanel,
  resolveLiveThoughtProcessStatus,
} from "../../shared/components/thoughtProcess";
import {
  emptyExcelAgentTimeline,
  hasExcelAgentTimelineContent,
  visibleExcelAgentHops,
  type ExcelAgentTimelineState,
} from "../agentTimeline";
import { ExcelAgentTimeline } from "./ExcelAgentTimeline";

const reasoningClassName =
  "rounded-lg border border-border/50 bg-background px-3 py-2.5 text-[12px] leading-relaxed text-muted-foreground";

function ReasoningCard({
  text,
  title,
}: {
  text: string;
  title?: string;
}) {
  return (
    <div className={title ? `mt-3 ${reasoningClassName}` : reasoningClassName}>
      {title ? (
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary">
          {title}
        </div>
      ) : null}
      <SimpleMarkdown text={text} compact />
    </div>
  );
}

/**
 * Excel adapter around the shared thought-process panel. Host-specific
 * content is the executor timeline, the Skills chip (which can appear
 * before the first hop), and an optional execution-summary markdown block.
 */
export const ExcelThoughtProcess: React.FC<{
  timeline?: ExcelAgentTimelineState;
  thinking?: string;
  streaming?: boolean;
  defaultOpen?: boolean;
  /** Live run: always render, even with no timeline/thinking yet. */
  live?: boolean;
  /** Live status string; an empty status falls back to "Thinking...". */
  loadingStatus?: string;
}> = ({
  timeline,
  thinking,
  streaming = false,
  defaultOpen = true,
  live = false,
  loadingStatus = "",
}) => {
  const hasTimeline = hasExcelAgentTimelineContent(timeline);
  const reasoning = thinking?.trim() ?? "";
  if (!live && !hasTimeline && !reasoning) return null;

  const liveStatus = resolveLiveThoughtProcessStatus({
    isLive: live,
    visibleHopCount: visibleExcelAgentHops(timeline).length,
    hasReasoning: Boolean(reasoning),
    loadingStatus,
  });

  return (
    <ThoughtProcessPanel
      defaultOpen={defaultOpen}
      liveStatus={liveStatus}
      liveIndicator="pulse"
    >
      {hasTimeline || streaming ? (
        <>
          <ExcelAgentTimeline
            timeline={timeline ?? emptyExcelAgentTimeline()}
            streaming={streaming}
          />
          {reasoning ? <ReasoningCard text={reasoning} title="Execution summary" /> : null}
        </>
      ) : reasoning ? (
        <ReasoningCard text={reasoning} />
      ) : null}
    </ThoughtProcessPanel>
  );
};
